import { useState } from "react";
import { Image } from "react-native";
import { IconButton, Menu, Text } from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import TooltipWrapper from "../TooltipWrapper";
import ChatMessageContent from "./ChatMessageContent";
import type { MessageType } from "./ChatMessages/types";

export type Props = {
	aiName: string;
	aiAvatar: string;
	type: MessageType;
	leadingIcons?: { icon: IconType; title?: string; onPress: () => void }[];
	textToSpeach?: React.ReactNode;
	trailingIcon?: {
		icon: IconType;
		onResponseThumbUp: () => void;
		onResponseThumbDown: () => void;
		onReportIssue: () => void;
		onRegenerateResponse: () => void;
	};
	isLastMessage?: boolean;
	isFirstMessage?: boolean;
};

const Message = ({
	type,
	leadingIcons,
	trailingIcon,
	aiAvatar,
	aiName,
	textToSpeach,
	// biome-ignore lint/correctness/noUnusedFunctionParameters: <explanation>
	// biome-ignore lint/correctness/noUnusedVariables: <explanation>
	isLastMessage = false,
	isFirstMessage = false,
}: Props) => {
	const theme = useAppTheme();
	const [copiedIconIndex, setCopiedIconIndex] = useState<number | null>(null);
	const [menuVisible, setMenuVisible] = useState(false);
	const handleCopy = (index: number) => {
		setCopiedIconIndex(index);
		setTimeout(() => {
			setCopiedIconIndex(null);
		}, 1500);
	};

	return (
		<Box gap={8} width="100%">
			<Box flexDirection="row" gap={8} alignItems="center">
				<Image
					source={{ uri: aiAvatar }}
					style={{
						width: 32,
						height: 32,
						borderRadius: 32 / 2,
					}}
				/>
				<Text
					variant="bodyMedium"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					{aiName}
				</Text>
			</Box>
			<ChatMessageContent type={type} />
			{type.type !== "processing" &&
				type.type !== "streaming" &&
				type.type !== "networkError" &&
				type.type !== "attemptingConnection" &&
				!isFirstMessage && (
					<Box flexDirection="row" alignItems="center" justifyContent="center">
						{leadingIcons?.map(({ icon, title, onPress }, index) => {
							const button = (
								<IconButton
									accessibilityLabel={title}
									onPress={() => {
										if (icon === "content-copy") {
											handleCopy(index);
										}
										onPress();
									}}
									iconColor={theme.colors.onSurfaceVariant}
									icon={copiedIconIndex === index ? "check" : icon}
									size={16}
								/>
							);
							return (
								<Box
									flexDirection="row"
									alignItems="center"
									justifyContent="center"
									width={32}
									height={32}
									key={icon}
								>
									{title ? (
										<TooltipWrapper title={title}>{button}</TooltipWrapper>
									) : (
										button
									)}
								</Box>
							);
						})}
						{textToSpeach}
						{trailingIcon && !isFirstMessage && (
							<Box flex={1} alignItems="flex-end" justifyContent="center">
								<>
									<IconButton
										onPress={() => setMenuVisible(true)}
										iconColor={theme.colors.onSecondaryContainer}
										icon={trailingIcon.icon}
										size={16}
									/>
									<Menu
										onDismiss={() => setMenuVisible(false)}
										visible={menuVisible}
										anchor={<Box width={1} height={1} />}
									>
										<Menu.Item
											title="Good response"
											leadingIcon="thumb-up-outline"
											onPress={() => {
												trailingIcon.onResponseThumbUp();
												setMenuVisible(false);
											}}
										/>
										<Menu.Item
											title="Bad response"
											leadingIcon="thumb-down-outline"
											onPress={() => {
												trailingIcon.onResponseThumbDown();
												setMenuVisible(false);
											}}
										/>
										<Menu.Item
											title="Report"
											leadingIcon="flag-outline"
											onPress={() => {
												trailingIcon.onReportIssue();
												setMenuVisible(false);
											}}
										/>
										{/* TODO: Add regenerate response on back end side */}
										{/* {isLastMessage && (
											<Menu.Item
												title="Regenerate response"
												leadingIcon="refresh"
												onPress={() => {
													trailingIcon.onRegenerateResponse();
													setMenuVisible(false);
												}}
											/>
										)} */}
									</Menu>
								</>
							</Box>
						)}
					</Box>
				)}
		</Box>
	);
};

export default Message;
